import "./JobApplicationFormComponent.scss";
import { Form, Formik, FormikHelpers } from "formik";
import { Form as AntdForm } from "antd";
import FormLabelComponent from "../../../components/ant-component/form-label/FormLabelComponent";
import FormikInputComponent from "../../../components/ant-component/formik-input-component/FormikInputComponent";
import FormikSelectDropdownComponent from "../../../components/ant-component/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../components/ant-component/button-component/ButtonComponent";
import React, { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { StateParams } from "../../../store/reducers";
import UploadImageComponent from "../../../components/upload-image-component/UploadImageComponent";
import { CommonService } from "../../../helpers";
import { ENV } from "../../../constants";

interface JobApplicationFormComponentProps {
    jobId: string;
    onCloseModal?: any;
}
const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Email is invalid").required("Email is required"),
    phone_number: Yup.string().required("Phone number is required"),
    hcp_type: Yup.string(),
    staff_specialty: Yup.string(),
});

const JobApplicationFormComponent = (props: JobApplicationFormComponentProps) => {
    const { jobId, onCloseModal } = props;
    const { user } = useSelector((state: StateParams) => state.auth);
    const { hcpTypes, specialitiesMasterList } = useSelector((state: StateParams) => state.meta);

    // Get URL query parameters (takes precedence over user data)
    const urlParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            email: params.get('email') || '',
            name: params.get('name') || ''
        };
    }, []);

    const specialtiesFormatted = useMemo(() => {
        return specialitiesMasterList?.map((item: any) => ({
            title: item.title,
            code: item.code
        })) || []
    }, [specialitiesMasterList])

    const initialValues = {
        name: urlParams.name || (user ? [user?.first_name, user?.last_name].filter(Boolean).join(' ') : ''),
        email: urlParams.email || (user?.email || ''),
        phone_number: user?.contact_number || '',
        hcp_type: '',
        staff_specialty: '',
        attachment: null,
    };


    const handleSubmit = useCallback((values: any, { setSubmitting, resetForm, setErrors }: FormikHelpers<any>) => {
        setSubmitting(true);
        const formData = new FormData();
        formData.append("name", values?.name || "");
        formData.append("email", values?.email?.toLowerCase() || "");
        formData.append("contact_number", values?.phone_number || "");
        if (values?.hcp_type) {
            formData.append("hcp_type", values?.hcp_type);
        }
        if (values?.staff_specialty) {
            formData.append("hcp_type_specialities", JSON.stringify([values?.staff_specialty]));
        }
        formData.append("job_id", jobId || '');
        if (values?.attachment) {
            formData.append("attachment", values?.attachment);
        }

        CommonService._api.post(ENV.API_URL + 'jobBoard/applyJob', formData).then((response: any) => {
            CommonService.showToast(response?.message, "success");
        }).catch((error: any) => {
            CommonService.showToast(error?.error || error.message || "Error", "error");
        }).finally(() => {
            setSubmitting(false);
            onCloseModal();
            resetForm();
        })
    }, [jobId, onCloseModal])


    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({ values, isSubmitting, setFieldValue }) => (
                <Form id="add-job-basic-details">
                    <AntdForm layout="vertical">
                        <div>
                            <FormikInputComponent
                                name="name"
                                size={'large'}
                                label={'Name'}
                                required={true}
                            />
                        </div>
                        <div>
                            <FormikInputComponent
                                name="email"
                                size={'large'}
                                label={'Email'}
                                required={true}
                            />
                        </div>
                        <div>
                            <FormikInputComponent
                                name="phone_number"
                                size={'large'}
                                label={'Phone Number'}
                                placeholder={'Enter phone number'}
                                required={true}
                            />
                        </div>
                        <div>
                            <FormikSelectDropdownComponent
                                name="hcp_type"
                                label="Staff Type"
                                placeholder="Select staff type"
                                required={false}
                                size="large"
                                options={Array.isArray(hcpTypes) ? hcpTypes.map((type: any) => ({
                                    title: type.title,
                                    code: type.code
                                })) : []}
                            />
                        </div>
                        <div>
                            <FormikSelectDropdownComponent
                                name="staff_specialty"
                                label="Staff Specialty"
                                placeholder="Select staff specialty (optional)"
                                size="large"
                                options={specialtiesFormatted}
                            />
                        </div>
                        <FormLabelComponent Label={"Resume"} />
                        <div className={'mrg-bottom-20'}>
                            <UploadImageComponent
                                isEdit={false}
                                selectedFiles={values?.attachment}
                                onClick={(file: any) => {
                                    setFieldValue('attachment', file)
                                }}
                            />
                        </div>
                    </AntdForm>
                    <div className={'file-wrapper'}>
                        <ButtonComponent
                            loading={isSubmitting}
                            type={'submit'}
                            color={'primary'}
                            variant={'solid'}
                            className={'file-wrapper'}
                            size={'large'}
                        >
                            Submit
                        </ButtonComponent>
                    </div>
                </Form>
            )}
        </Formik>
    )
}
export default JobApplicationFormComponent;